"use client";

import { useEffect, useState } from "react";
import { Send, RefreshCw, X } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

const ROLE_COLORS: Record<string, string> = {
  ceo:      "#C9A84C",
  cfo:      "#22c55e",
  cmo:      "#f59e0b",
  coo:      "#3b82f6",
  analyst:  "#8b5cf6",
  hr:       "#ec4899",
  general:  "#9ca3af",
};

function AgentCard({ agent, onTask, size = "normal" }: { agent: any; onTask: (a: any) => void; size?: "large" | "normal" }) {
  const color = ROLE_COLORS[agent.role] || "#9ca3af";
  const isRunning = agent.status === "running";
  const isLarge = size === "large";

  return (
    <div style={{
      background: "var(--bg-card)",
      borderRadius: "var(--radius)",
      border: "1px solid var(--border)",
      boxShadow: "var(--shadow)",
      overflow: "hidden",
      transition: "transform 150ms ease, box-shadow 150ms ease",
    }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLElement).style.boxShadow = "var(--shadow-md)"; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLElement).style.boxShadow = "var(--shadow)"; }}
    >
      {/* Barra de color */}
      <div style={{ height: "3px", background: color }} />

      <div style={{ padding: isLarge ? "28px" : "20px" }}>
        {/* Avatar + estado */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "14px" }}>
          <div style={{
            width: isLarge ? "64px" : "52px",
            height: isLarge ? "64px" : "52px",
            borderRadius: isLarge ? "18px" : "14px",
            background: color + "18",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: isLarge ? "30px" : "24px",
            flexShrink: 0,
          }}>
            {agent.emoji}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{
              width: "7px", height: "7px", borderRadius: "50%",
              background: isRunning ? "#22c55e" : "#d1d5db",
              display: "inline-block",
              animation: isRunning ? "pulse-dot 2s infinite" : "none",
            }} />
            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
              {isRunning ? "Trabajando" : "Disponible"}
            </span>
          </div>
        </div>

        {/* Info */}
        <div style={{ marginBottom: "14px" }}>
          <div style={{ fontWeight: 700, fontSize: isLarge ? "18px" : "15px", color: "var(--text)" }}>{agent.name}</div>
          <div style={{
            display: "inline-block", marginTop: "4px",
            fontSize: "11px", fontWeight: 600, letterSpacing: "0.04em",
            color: color, background: color + "15",
            padding: "2px 10px", borderRadius: "20px",
          }}>
            {agent.role_label}
          </div>
        </div>

        <p style={{ fontSize: "12px", color: "var(--text-muted)", lineHeight: 1.6, marginBottom: "16px" }}>
          {agent.description}
        </p>

        <button
          onClick={() => onTask(agent)}
          style={{
            width: "100%", padding: "9px 0",
            background: "var(--bg-dark)", color: "var(--text-on-dark)",
            border: "none", borderRadius: "9px",
            fontSize: "12px", fontWeight: 500, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            transition: "background 150ms",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "#1e3048")}
          onMouseLeave={e => (e.currentTarget.style.background = "var(--bg-dark)")}
        >
          <Send size={12} /> Asignar tarea
        </button>
      </div>
    </div>
  );
}

function TaskModal({ agent, onClose, onSend }: { agent: any; onClose: () => void; onSend: (msg: string, channel: string) => Promise<void> }) {
  const [msg, setMsg] = useState("");
  const [channel, setChannel] = useState("telegram");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const handleSend = async () => {
    if (!msg.trim()) return;
    setSending(true);
    await onSend(msg, channel);
    setSending(false);
    setDone(true);
    setTimeout(onClose, 1200);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,25,35,0.55)",
      zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
      backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg-card)", borderRadius: "20px",
        padding: "32px", width: "500px", maxWidth: "90vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        animation: "fade-in 0.2s ease",
      }} onClick={e => e.stopPropagation()}>

        {/* Header modal */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "14px",
              background: (ROLE_COLORS[agent.role] || "#9ca3af") + "18",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "22px",
            }}>
              {agent.emoji}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "16px" }}>Tarea para {agent.name}</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{agent.role_label}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-light)", padding: "4px" }}>
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div style={{ textAlign: "center", padding: "20px 0", fontSize: "16px", color: "#22c55e", fontWeight: 500 }}>
            ✅ Tarea enviada a {agent.name}
          </div>
        ) : (
          <>
            <textarea
              value={msg}
              onChange={e => setMsg(e.target.value)}
              placeholder={`¿Qué quieres que haga ${agent.name}?`}
              autoFocus
              rows={5}
              style={{
                width: "100%", padding: "14px 16px",
                background: "var(--bg)", border: "1px solid var(--border)",
                borderRadius: "12px", fontSize: "14px", color: "var(--text)",
                resize: "vertical", outline: "none", fontFamily: "inherit",
                lineHeight: 1.6,
              }}
            />

            {/* Canal de entrega */}
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "8px" }}>Entregar resultado en:</div>
              <div style={{ display: "flex", gap: "8px" }}>
                {[
                  { value: "telegram", label: "📱 Telegram" },
                  { value: "inbox",    label: "📥 Bandeja" },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setChannel(opt.value)} style={{
                    padding: "7px 14px", borderRadius: "8px", fontSize: "12px",
                    cursor: "pointer", fontWeight: channel === opt.value ? 600 : 400,
                    background: channel === opt.value ? "var(--bg-dark)" : "var(--bg-muted)",
                    color: channel === opt.value ? "white" : "var(--text-muted)",
                    border: "1px solid " + (channel === opt.value ? "var(--bg-dark)" : "var(--border)"),
                    transition: "all 150ms",
                  }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{
                padding: "10px 20px", background: "var(--bg-muted)",
                border: "1px solid var(--border)", borderRadius: "10px",
                fontSize: "13px", cursor: "pointer", color: "var(--text)",
              }}>
                Cancelar
              </button>
              <button onClick={handleSend} disabled={sending || !msg.trim()} style={{
                padding: "10px 24px", background: "var(--bg-dark)",
                border: "none", borderRadius: "10px", color: "white",
                fontSize: "13px", fontWeight: 500, cursor: "pointer",
                opacity: sending || !msg.trim() ? 0.6 : 1,
                display: "flex", alignItems: "center", gap: "7px",
              }}>
                {sending ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
                {sending ? "Enviando..." : "Asignar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function TeamPage() {
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);

  useEffect(() => {
    fetch(`${API}/agents`).then(r => r.json()).then(setAgents).catch(() => {});
  }, []);

  const leo  = agents.find(a => a.id === "leo");
  const mia  = agents.find(a => a.id === "mia");
  const team = agents.filter(a => a.id !== "leo" && a.id !== "mia");

  const handleSendTask = async (msg: string, channel: string) => {
    if (!selectedAgent) return;
    await fetch(`${API}/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: msg.slice(0, 80),
        description: msg,
        agent_id: selectedAgent.id,
        delivery_channel: channel,
      }),
    }).catch(() => {});
  };

  return (
    <>
      <div className="animate-fade-in" style={{ width: "100%" }}>

        {/* Header */}
        <div style={{ marginBottom: "40px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: 700, color: "var(--text)" }}>Meet the Team</h1>
          <p style={{ color: "var(--text-muted)", marginTop: "6px" }}>
            Tu equipo de agentes IA — {agents.length} miembros activos
          </p>
        </div>

        {/* Didac (fundador) */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "0" }}>
          <div style={{
            background: "var(--bg-dark)", borderRadius: "var(--radius)",
            padding: "18px 32px",
            display: "inline-flex", alignItems: "center", gap: "14px",
            boxShadow: "var(--shadow-md)",
          }}>
            <div style={{
              width: "44px", height: "44px", borderRadius: "50%",
              background: "var(--accent)", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "18px", fontWeight: 700, color: "#0F1923",
            }}>D</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "16px", color: "var(--text-on-dark)" }}>Didac</div>
              <div style={{ fontSize: "12px", color: "var(--text-on-dark-muted)" }}>Fundador · Board</div>
            </div>
          </div>
        </div>

        {/* Línea vertical */}
        <div style={{ display: "flex", justifyContent: "center", margin: "0" }}>
          <div style={{ width: "1px", height: "36px", background: "var(--border)" }} />
        </div>

        {/* Leo (CEO) centrado + Mia en posición absoluta al lado */}
        {leo && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "0", position: "relative" }}>
            <div style={{ width: "320px" }}>
              <AgentCard agent={leo} onTask={setSelectedAgent} size="large" />
            </div>
            {mia && (
              <div onClick={() => setSelectedAgent(mia)} style={{
                position: "absolute", left: "calc(50% + 175px)", top: "8px",
                background: "var(--bg-card)", border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)", padding: "10px 14px",
                display: "flex", alignItems: "center", gap: "8px",
                cursor: "pointer", transition: "all 150ms",
                boxShadow: "var(--shadow)", whiteSpace: "nowrap",
              }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-muted)")}
                onMouseLeave={e => (e.currentTarget.style.background = "var(--bg-card)")}>
                <div style={{ fontSize: "18px" }}>{mia.emoji}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "12px" }}>{mia.name}</div>
                  <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>{mia.role_label}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Línea + ramificación */}
        <div style={{ display: "flex", justifyContent: "center", margin: "0" }}>
          <div style={{ width: "1px", height: "36px", background: "var(--border)" }} />
        </div>

        {/* Línea horizontal */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "0" }}>
          <div style={{
            height: "1px", background: "var(--border)",
            width: `calc(100% / ${Math.max(team.length, 1)} * ${Math.max(team.length - 1, 1)})`,
            maxWidth: "80%",
          }} />
        </div>

        {/* Líneas bajando */}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${team.length}, 1fr)`, marginBottom: "0", maxWidth: "80%", margin: "0 auto" }}>
          {team.map((_, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "1px", height: "24px", background: "var(--border)" }} />
            </div>
          ))}
        </div>

        {/* Equipo */}
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(team.length, 5)}, 1fr)`,
          gap: "16px",
          maxWidth: "100%",
        }}>
          {team.map(agent => (
            <AgentCard key={agent.id} agent={agent} onTask={setSelectedAgent} />
          ))}
        </div>

      </div>

      {/* Modal */}
      {selectedAgent && (
        <TaskModal
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
          onSend={handleSendTask}
        />
      )}
    </>
  );
}
